<script>
import { mapGetters } from 'vuex'
import { isNullOrEmptyArray } from '@helpers/array-helpers'

export default {
  model: {
    prop: 'selected',
    event: 'save',
  },
  props: {
    title: {
      type: String,
      default: 'Select Filter',
    },
    treeData: {
      type: Array,
      default: () => [],
    },
    selected: {
      type: Array,
      default: () => [],
      description:
        'Syncable prop that allows control over a list of selected nodes',
    },
    filters: {
      type: Array,
      default: () => [],
      description:
        'Provide a list of filter predicates which return true or false to the tree data set. e.g AdminId == 123',
    },
    hideNoChildNodes: {
      type: Boolean,
      default: false,
      description:
        'handy when using multi-select and only care about bottom level nodes',
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    selectPreview: {
      type: Boolean,
      default: false,
    },
    activatable: {
      type: Boolean,
      default: false,
      description:
        'used for single select, should not be used at the same time as selectable',
    },
    dense: {
      type: Boolean,
      default: false,
    },
    prependIcon: {
      type: String,
      required: false,
      default: null,
    },
    prependIconInner: {
      type: String,
      required: false,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      savedSelectionForInput: [],
      currentlySelected: [],
      isDialogShowing: false,
      search: null,
      active: [],
    }
  },
  computed: {
    ...mapGetters({
      divisionList: 'staff/divTree',
    }),
    itemsForSelectInput() {
      if (!isNullOrEmptyArray(this.currentlySelected)) {
        return this.currentlySelected.map((n) => n.title)
      }
      if (!isNullOrEmptyArray(this.selected)) {
        return this.selected.map((n) => n.title)
      }
      return []
    },
    menuPropsHideNativeDropdown() {
      // Since we are putting select options into a v-dialog, we want to hide the native select options
      return { 'z-index': -100 }
    },
    dialogWidth() {
      return this.selectPreview ? 800 : 500
    },
  },
  watch: {
    isDialogShowing(val, oldVal) {
      if (val !== oldVal) {
        this.currentlySelected = this.selected
      }
    },
    selected(val, oldVal) {
      if (val && val !== oldVal) {
        this.currentlySelected = this.selected
      }
    },
  },
  methods: {
    singleSelection(node) {
      this.currentlySelected = node
    },
    saveSelection() {
      this.$emit('save', this.currentlySelected)
      this.isDialogShowing = false
      this.savedSelectionForInput = this.currentlySelected
      this.active = this.currentlySelected ? [this.currentlySelected] : []
    },
    resetSelection() {
      this.currentlySelected = []
      this.active = []
    },
    handleSelectClick() {
      if (this.isDialogShowing === false) {
        this.isDialogShowing = true
      }
    },
    searchTree(node, key) {
      if (node.key === key) {
        return node
      } else if (node.children != null) {
        let result = null
        for (let i = 0; result == null && i < node.children.length; i++) {
          result = this.searchTree(node.children[i], key)
        }
        return result
      }
      return null
    },
    cancelSelection() {
      this.currentlySelected = []
      this.savedSelectionForInput = this.currentlySelected
      this.active = this.currentlySelected ? [this.currentlySelected] : []
      this.currentlySelected = this.selected
    },
    close() {
      this.cancelSelection()
      this.isDialogShowing = false
    },
  },
}
</script>

<template>
  <div class="base-tree-selector">
    <v-select
      ref="treeSelectInput"
      :items="itemsForSelectInput"
      :value="itemsForSelectInput"
      :label="title"
      small-chips
      multiple
      :menu-props="menuPropsHideNativeDropdown"
      v-bind="$attrs"
      @click="handleSelectClick"
    >
      <template v-slot:selection="{ item, index }">
        <v-chip v-if="index === 0" small>
          <span>{{ item }}</span>
        </v-chip>
        <span v-if="index === 1" class="grey--text text-caption">
          (+{{ itemsForSelectInput.length - 1 }} others)
        </span>
      </template>
    </v-select>
    <v-dialog
      id="tree-dialog"
      v-model="isDialogShowing"
      content-class="overflow-y-auto"
      :width="dialogWidth"
      @click:outside="cancelSelection"
      @keydown.esc="cancelSelection"
    >
      <v-card :loading="loading" max-height="700">
        <div class="text-h5 grey lighten-3 pa-5">
          <div class="d-flex justify-space-between">
            {{ title }}
            <v-spacer />
            <v-btn icon @click="close">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
          <v-text-field
            v-if="searchable"
            v-model="search"
            :label="$t('treeSelector.searchLabel')"
            clearable
            hide-details
            clear-icon="mdi-close-circle"
          />
        </div>
        <v-card-text class="pa-0 flex-grow-1">
          <div class="d-flex tree-content-container overflow-x-hidden">
            <div
              :class="
                selectPreview
                  ? 'tree-content-scrollable'
                  : 'flex-grow-1 overflow-y-auto overflow-x-hidden'
              "
            >
              <v-treeview
                v-model="currentlySelected"
                :activatable="activatable"
                :active.sync="active"
                :items="treeData"
                :search="search"
                :selectable="selectable"
                :dense="dense"
                return-object
                item-key="key"
                item-text="title"
                @update:active="singleSelection"
              ></v-treeview>
            </div>

            <v-divider v-if="selectPreview" vertical></v-divider>

            <div v-if="selectPreview" class="tree-content-scrollable">
              <v-card-text>
                <div v-if="currentlySelected && currentlySelected.length > 0">
                  <v-chip
                    v-for="(selection, i) in currentlySelected"
                    :key="i"
                    color="grey"
                    dark
                    small
                    class="ma-1"
                  >
                    <v-icon v-if="selection.icon" left small>
                      {{ selection.icon }}
                    </v-icon>
                    {{ selection.title }}
                  </v-chip>
                </div>
                <div
                  v-else
                  key="title"
                  class="text-h6 font-weight-light grey--text pa-4 text-center"
                >
                  {{ $t('treeSelector.previewEmptyDisplayText') }}
                </div>
              </v-card-text>
            </div>
          </div>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions>
          <v-btn text @click="resetSelection">
            {{ $t('treeSelector.clearBtn') }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="saveSelection">
            {{ $t('treeSelector.saveBtn') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped lang="scss">
.div-selector {
  margin-bottom: 20px;
}

.tree-content-scrollable {
  width: 50%;
  overflow-x: hidden;
  overflow-y: auto;
}

.tree-content-container {
  max-height: 500px;
  overflow-y: auto;
}
</style>
